import { useQuery } from "@tanstack/react-query";
import api, { num } from "@/lib/api";
import { PageHeader, Panel, Loading, StatCard, CHART_COLORS } from "@/components/kit";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";
import { Target, TrendingUp, Boxes } from "lucide-react";

export default function Sales() {
  const { data, isLoading } = useQuery({
    queryKey: ["dash-sales"],
    queryFn: async () => (await api.get("/dashboard/sales")).data,
  });
  if (isLoading || !data) return <Loading label="Loading sales dashboard…" />;
  const tva = data.target_vs_actual;

  return (
    <div>
      <PageHeader title="Sales Dashboard" subtitle="Volume performance, target achievement and top dealers" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard testid="sales-kpi-target" label="Total Target" value={`${num(tva.target)} MT`} icon={Target} tone="navy" />
        <StatCard testid="sales-kpi-actual" label="Actual Volume" value={`${num(tva.actual)} MT`} icon={Boxes} tone="gold" />
        <StatCard testid="sales-kpi-ach" label="Achievement" value={`${tva.achievement_pct}%`} icon={TrendingUp} tone="emerald" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel testid="panel-region-volume" title="Volume by Region" subtitle="Dispatched MT per region">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={data.region_volume} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <YAxis type="category" dataKey="region" tick={{ fontSize: 12, fill: "#334155" }} width={70} />
              <Tooltip formatter={(v) => `${num(v)} MT`} />
              <Bar dataKey="volume" radius={[0, 8, 8, 0]} barSize={26}>
                {data.region_volume.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel testid="panel-top-dealers" title="Top 10 Dealers" subtitle="By dispatched volume">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 text-xs uppercase tracking-wider border-b border-slate-100">
                  <th className="py-2 pr-2">#</th><th className="py-2 pr-2">Dealer</th>
                  <th className="py-2 pr-2">Code</th><th className="py-2 text-right">Volume (MT)</th>
                </tr>
              </thead>
              <tbody>
                {data.top_dealers.map((d, i) => (
                  <tr key={i} className="drill-row border-b border-slate-50" data-testid={`top-dealer-${i}`}>
                    <td className="py-2.5 pr-2 text-slate-400 font-mono">{i + 1}</td>
                    <td className="py-2.5 pr-2 font-medium text-slate-800">{d.name}</td>
                    <td className="py-2.5 pr-2 text-slate-500 font-mono text-xs">{d.code}</td>
                    <td className="py-2.5 text-right font-semibold text-[#00529B]">{num(d.volume)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  );
}
